import { ArrowLeft, Camera, Info } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { message } from "antd";
import welcomeBg from "../../assets/homebg.png";
import ProfileService from "../../services/ProfileService";
import { useQueryClient } from "@tanstack/react-query";
import { UploadService } from "@/services/APIs";
import { Skeleton } from "../../components/ui/skeleton";

const EditProfilePage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [profileImage, setProfileImage] = useState(
    localStorage.getItem("profileImage") ?? ""
  );
  const [uploadedPhotoId, setUploadedPhotoId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    gender: "Male",
    dateOfBirth: "",
    heightFeet: "5",
    heightInches: "5",
    heightCm: "170",
    height: "170",
    heightUnit: "cm",
    weight: "55",
    weightUnit: "kg",
    ethnicity: "",
  });

  const [dobError, setDobError] = useState<string>("");

  // Get today's date in YYYY-MM-DD format for max date validation
  const getTodayDate = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Get min date (150 years back)
  const getMinDate = (): string => {
    const today = new Date();
    const year = today.getFullYear() - 150;
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const validateDob = (dateStr: string): string => {
    if (!dateStr) return "";
    const today = new Date();
    const dob = new Date(dateStr);
    if (dob > today) return "Please select a date that is not in the future";
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    if (age < 13) return "You must be at least 13 years old";
    if (age > 150) return "Age cannot be more than 150 years";
    return "";
  };

  // Convert API date format (DD/MM/YYYY) to HTML date format (YYYY-MM-DD)
  const convertDateToHTMLFormat = (dateStr: string): string => {
    if (!dateStr) {
      return "";
    }
    try {
      const [day, month, year] = dateStr.split("/");
      if (!day || !month || !year) {
        return "";
      }
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    } catch (error) {
      // Date conversion failed, return empty string
      return "";
    }
  };

  // Convert HTML date format (YYYY-MM-DD) to API format (DD/MM/YYYY)
  const convertDateToAPIFormat = (dateStr: string): string => {
    if (!dateStr) {
      return "";
    }
    try {
      const [year, month, day] = dateStr.split("-");
      if (!year || !month || !day) {
        return "";
      }
      return `${day}/${month}/${year}`; // DD/MM/YYYY format
    } catch (error) {
      // Date conversion failed, return empty string
      return "";
    }
  };

  // Map API race values to display values
  const mapRaceFromAPI = (race: string): string => {
    const mapping: { [key: string]: string } = {
      black_african_american: "Black or African American",
      american_indian_alaska_native: "American Indian or Alaska Native",
      native_hawaiian_pacific_islander: "Native Hawaiian/Pacific Islander",
      middle_eastern_north_african: "Middle Eastern or North African",
      white: "White",
      asian: "Asian",
      other: "Other",
    };
    return mapping[race] || "Other";
  };

  // Map display race values to API values
  const mapRaceToAPI = (race: string): string => {
    const mapping: { [key: string]: string } = {
      "Black or African American": "black_african_american",
      "American Indian or Alaska Native": "american_indian_alaska_native",
      "Native Hawaiian/Pacific Islander": "native_hawaiian_pacific_islander",
      "Middle Eastern or North African": "middle_eastern_north_african",
      White: "white",
      Asian: "asian",
      Other: "other",
    };
    return mapping[race] || "other";
  };

  // Load profile data and preferences from API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Load profile data
        const response = await ProfileService.getEditProfileData();

        if (response?.profile) {
          const profile = response.profile;

          // Use units from profile API response instead of separate preferences call
          const weightUnit = profile.weight_in || "kg";
          const heightUnit = profile.height_in || "cm";

          // Convert weight based on preferred unit
          const weightValue =
            weightUnit === "lbs"
              ? (
                profile.weight_lbs ||
                (profile.weight_kg ? profile.weight_kg * 2.205 : 55)
              ).toString()
              : (
                profile.weight_kg ||
                (profile.weight_lbs ? profile.weight_lbs / 2.205 : 55)
              ).toString();

          // Convert height based on preferred unit
          let heightValue = "";
          let heightFeet = profile.height_ft?.toString() || "5";
          let heightInches = profile.height_inches?.toString() || "5";
          let heightCm = profile.height_cm?.toString() || "170";

          if (heightUnit === "ft_in") {
            // If preference is ft_in, use feet/inches
            if (profile.height_cm) {
              const totalInches = profile.height_cm / 2.54;
              heightFeet = Math.floor(totalInches / 12).toString();
              heightInches = Math.round(totalInches % 12).toString();
            }
            heightValue = `${heightFeet}'${heightInches}"`;
          } else {
            // If preference is cm, convert feet/inches to cm if needed
            if (profile.height_ft && profile.height_inches) {
              heightCm = (
                (profile.height_ft * 12 + profile.height_inches) *
                2.54
              ).toString();
            }
            heightValue = heightCm;
          }

          setFormData({
            name: profile.full_name || "",
            email: profile.email || "",
            gender: profile.gender
              ? profile.gender.charAt(0).toUpperCase() + profile.gender.slice(1)
              : "Male",
            dateOfBirth: convertDateToHTMLFormat(profile.date_of_birth || ""),
            heightFeet: heightFeet,
            heightInches: heightInches,
            heightCm: heightCm,
            height: heightValue,
            heightUnit: heightUnit,
            weight: String(Math.round(parseFloat(weightValue))),
            weightUnit: weightUnit,
            ethnicity: mapRaceFromAPI(profile.race || ""),
          });

          if (profile.photo) {
            setProfileImage(profile.photo);
          }
        }
      } catch (error) {
        message.error("Error loading data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        message.error("Image size should be less than 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = async () => {
        const result = reader.result as string;
        setProfileImage(result);
        localStorage.setItem("profileImage", result);

        try {
          const uploadResponse = await UploadService.uploadFiles({
            folderName: "profile",
            files: [file],
          });

          const uploadItem = Array.isArray(uploadResponse)
            ? uploadResponse[0]
            : uploadResponse?.data?.[0] ||
              uploadResponse?.files?.[0] ||
              uploadResponse;

          const photoId = uploadItem?.id || uploadItem?.file_utility_id || null;
          const photoUrl =
            uploadItem?.url ||
            uploadItem?.file_url ||
            uploadItem?.original_file ||
            null;

          if (photoId) {
            setUploadedPhotoId(String(photoId));
          } else {
            setUploadedPhotoId(null);
            message.warning(
              "Photo ID not received from server. Profile photo may not save on backend."
            );
          }

          if (photoUrl) {
            setProfileImage(photoUrl);
            localStorage.setItem("profileImage", photoUrl);
          }
        } catch (_error) {
          setUploadedPhotoId(null);
          message.error("Photo upload failed. Please try again.");
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (dobError) {
      message.error(dobError);
      return;
    }
    try {
      setSaving(true);

      // Prepare API payload
      const profilePayload: any = {
        full_name: formData.name,
        email: formData.email,
        gender: formData.gender.toLowerCase(),
        date_of_birth: convertDateToAPIFormat(formData.dateOfBirth),
        height_in: formData.heightUnit, // Use user's preferred height unit
        // Send height values based on preferred unit - set others to null
        height_ft:
          formData.heightUnit === "ft_in"
            ? parseInt(formData.heightFeet) || 0
            : null,
        height_inches:
          formData.heightUnit === "ft_in"
            ? parseInt(formData.heightInches) || 0
            : null,
        height_cm:
          formData.heightUnit === "cm"
            ? parseInt(formData.heightCm) || 0
            : null,
        weight_in: formData.weightUnit,
        // Send weight values based on preferred unit - set other to null
        weight_kg:
          formData.weightUnit === "kg"
            ? parseFloat(formData.weight) || 0
            : null,
        weight_lbs:
          formData.weightUnit === "lbs"
            ? parseFloat(formData.weight) || 0
            : null,
        race: mapRaceToAPI(formData.ethnicity),
      };

      if (uploadedPhotoId) {
        profilePayload.photo = uploadedPhotoId;
      }

      await ProfileService.updateProfile(profilePayload);

      // Also save to localStorage as backup
      localStorage.setItem("userProfile", JSON.stringify(formData));

      // Invalidate home page data to trigger refresh with new BMI data
      queryClient.invalidateQueries({ queryKey: ["homePageData"] });

      // message.success("Profile updated successfully!");
      navigate("/glucera/profilepage");
    } catch (error) {
      message.error("Error saving profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {loading ? (
          <div className="space-y-4 pt-2">
            {/* Header skeleton */}
            <div className="flex items-center justify-between">
              <Skeleton className="h-10 w-10 rounded-xl" />
              <Skeleton className="h-6 w-28 rounded-lg" />
              <div className="w-10" />
            </div>
            {/* Avatar skeleton */}
            <div className="rounded-2xl border border-border bg-card p-6 shadow-lg flex flex-col items-center gap-3">
              <Skeleton className="h-24 w-24 rounded-full" />
              <Skeleton className="h-8 w-32 rounded-lg" />
            </div>
            {/* Form card skeleton */}
            <div className="rounded-2xl border border-border bg-card p-5 shadow-lg space-y-4">
              <Skeleton className="h-5 w-40 rounded-md" />
              <Skeleton className="h-10 w-full rounded-xl" />
              <Skeleton className="h-10 w-full rounded-xl" />
              <Skeleton className="h-10 w-full rounded-xl" />
              <Skeleton className="h-10 w-full rounded-xl" />
              <Skeleton className="h-10 w-full rounded-xl" />
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between pt-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/glucera/profilepage")}
                className="rounded-xl bg-card/80 hover:bg-card"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="text-center">
                <p className="text-sm font-medium text-muted-foreground">My Account</p>
                <h1 className="text-2xl font-semibold text-foreground">Edit Profile</h1>
              </div>
              <div className="w-10" />
            </div>

            {/* Profile Picture */}
            <Card className="rounded-2xl border border-border bg-card p-6 shadow-lg">
              <div className="flex flex-col items-center">
                <div className="relative">
                  <div className="mb-4 h-24 w-24 overflow-hidden rounded-full border-4 border-blue-100 bg-gradient-brand dark:border-blue-900">
                    {profileImage ? (
                      <img
                        src={profileImage}
                        alt="Profile"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-title1 font-bold text-white">
                        {formData.name
                          .split(" ")
                          .map((n: string) => n[0])
                          .join("")}
                      </div>
                    )}
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-lg border-border bg-card hover:bg-muted"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Camera className="mr-2 h-4 w-4" />
                  Change Photo
                </Button>
              </div>
            </Card>

            {/* Personal Information */}
            <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
              <h3 className="mb-4 text-callout font-semibold text-foreground">
                Personal Information
              </h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-muted-foreground">
                    Full Name
                  </Label>
                  <Input
                    id="name"
                    placeholder="e.g., John Doe"
                    value={formData.name}
                    onChange={e => {
                      // Block emojis and special characters — allow only letters, spaces, hyphens, apostrophes, dots
                      const val = e.target.value.replace(/[^\p{L}\s\-'.]/gu, "");
                      setFormData({ ...formData, name: val });
                    }}
                    className="rounded-xl border-border"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-muted-foreground">
                    Email
                  </Label>
                  <div
                    id="email"
                    className="flex min-h-10 w-full items-center break-all rounded-xl border border-border bg-muted/50 px-3 py-2 text-subhead text-muted-foreground"
                    title={formData.email}
                  >
                    {formData.email || "—"}
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="gender" className="text-muted-foreground">
                      Gender
                    </Label>
                    <div className="relative">
                      <select
                        id="gender"
                        value={formData.gender}
                        onChange={e =>
                          setFormData({ ...formData, gender: e.target.value })
                        }
                        className="flex h-10 w-full rounded-xl border border-border bg-background px-3 py-2 text-subhead placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-black disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="dateOfBirth"
                      className="text-muted-foreground"
                    >
                      Date of Birth
                    </Label>
                    <Input
                      id="dateOfBirth"
                      type="date"
                      value={formData.dateOfBirth}
                      max={getTodayDate()}
                      min={getMinDate()}
                      onChange={e => {
                        const val = e.target.value;
                        setFormData({ ...formData, dateOfBirth: val });
                        setDobError(validateDob(val));
                      }}
                      className="rounded-xl border-border px-4 py-2 text-subhead text-left"
                      style={{
                        position: "relative",
                        WebkitAppearance: "none",
                        appearance: "none",
                        textAlign: "left",
                        display: "block",
                        width: "100%",
                      }}
                    />
                    {!formData.dateOfBirth && !dobError && (
                      <div className="mt-1 flex items-start gap-1.5 text-caption1 text-muted-foreground">
                        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-500" />
                        <span>
                          Tap to pick your date of birth. You must be at least 13
                          years old.
                        </span>
                      </div>
                    )}
                    {dobError && (
                      <p className="mt-1 text-caption1 text-destructive">{dobError}</p>
                    )}
                    {formData.dateOfBirth && !dobError && (() => {
                      const dob = new Date(formData.dateOfBirth);
                      const today = new Date();
                      let age = today.getFullYear() - dob.getFullYear();
                      const m = today.getMonth() - dob.getMonth();
                      if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
                      return age >= 13 ? (
                        <p className="mt-1 text-caption1 text-green-600 dark:text-green-400">✓ Age: {age} years</p>
                      ) : null;
                    })()}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">
                    Height ({formData.heightUnit === "cm" ? "Cms" : "Ft-Inch"})
                  </Label>
                  {formData.heightUnit === "cm" ? (
                    <Input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={formData.heightCm}
                      onChange={e => {
                        const cmValue = e.target.value;
                        if (cmValue !== "" && !/^\d{1,4}$/.test(cmValue)) return;
                        const totalInches = parseFloat(cmValue) / 2.54;
                        const feet = Math.floor(totalInches / 12).toString();
                        const inches = Math.round(totalInches % 12).toString();
                        setFormData({
                          ...formData,
                          heightCm: cmValue,
                          heightFeet: feet,
                          heightInches: inches,
                          height: cmValue,
                        });
                      }}
                      onKeyDown={e => {
                        if (e.key === "-" || e.key === "." || e.key === "e" || e.key === "E") e.preventDefault();
                      }}
                      placeholder="170"
                      className="rounded-xl border-border"
                    />
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex items-center rounded-xl border border-input bg-background focus-within:border-black">
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={formData.heightFeet}
                          onChange={e => {
                            const feetValue = e.target.value;
                            if (feetValue !== "" && (!/^\d+$/.test(feetValue) || Number(feetValue) > 10)) return;
                            const cmValue = (
                              (parseFloat(feetValue || "0") * 12 +
                                parseFloat(formData.heightInches || "0")) *
                              2.54
                            ).toString();
                            setFormData({
                              ...formData,
                              heightFeet: feetValue,
                              heightCm: cmValue,
                              height: `${feetValue}'${formData.heightInches}"`,
                            });
                          }}
                          onKeyDown={e => {
                            if (e.key === "-" || e.key === "." || e.key === "e" || e.key === "E") e.preventDefault();
                          }}
                          placeholder="0"
                          className="w-full rounded-xl border-none bg-transparent py-2 pl-3 pr-1 text-subhead text-foreground placeholder:text-muted-foreground focus:outline-none"
                        />
                        <span className="pr-3 text-subhead font-bold text-foreground">'</span>
                      </div>
                      <div className="flex items-center rounded-xl border border-input bg-background focus-within:border-black">
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={formData.heightInches}
                          onChange={e => {
                            const inchesValue = e.target.value;
                            if (inchesValue !== "" && (!/^\d+$/.test(inchesValue) || Number(inchesValue) >= 12)) return;
                            const cmValue = (
                              (parseFloat(formData.heightFeet || "0") * 12 +
                                parseFloat(inchesValue || "0")) *
                              2.54
                            ).toString();
                            setFormData({
                              ...formData,
                              heightInches: inchesValue,
                              heightCm: cmValue,
                              height: `${formData.heightFeet}'${inchesValue}"`,
                            });
                          }}
                          onKeyDown={e => {
                            if (e.key === "-" || e.key === "." || e.key === "e" || e.key === "E") e.preventDefault();
                          }}
                          placeholder="0"
                          className="w-full rounded-xl border-none bg-transparent py-2 pl-3 pr-1 text-subhead text-foreground placeholder:text-muted-foreground focus:outline-none"
                        />
                        <span className="pr-3 text-subhead font-bold text-foreground">''</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">
                    Weight ({formData.weightUnit === "kg" ? "Kg" : "Lbs"})
                  </Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={formData.weight}
                    onChange={e => {
                      const val = e.target.value;
                      if (val !== "" && !/^\d{1,3}$/.test(val)) return;
                      setFormData({ ...formData, weight: val });
                    }}
                    onKeyDown={e => {
                      if (e.key === "-" || e.key === "." || e.key === "e" || e.key === "E") e.preventDefault();
                    }}
                    placeholder={formData.weightUnit === "kg" ? "68" : "150"}
                    className="rounded-xl border-border"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ethnicity" className="text-muted-foreground">
                    Race/Ethnicity
                  </Label>
                  <div className="relative">
                    <select
                      id="ethnicity"
                      value={formData.ethnicity}
                      onChange={e =>
                        setFormData({ ...formData, ethnicity: e.target.value })
                      }
                      className="flex h-10 w-full rounded-xl border border-border bg-background px-3 py-2 text-subhead placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-black disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="White">White</option>
                      <option value="Black or African American">
                        Black or African American
                      </option>
                      <option value="Asian">Asian</option>
                      <option value="American Indian or Alaska Native">
                        American Indian or Alaska Native
                      </option>
                      <option value="Native Hawaiian/Pacific Islander">
                        Native Hawaiian/Pacific Islander
                      </option>
                      <option value="Middle Eastern or North African">
                        Middle Eastern or North African
                      </option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>
            </Card>

            {/* Save Button */}
            <Button
              onClick={handleSave}
              disabled={saving || loading || !!dobError}
              className="h-12 w-full rounded-xl bg-gradient-primary text-primary-foreground shadow-primary hover:opacity-90 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default EditProfilePage;
